import * as React from "react";
import { Box, Chip, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Activity } from "lucide-react";
import { listJobs, cancelJob, type JobSummary, type JobStatus } from "../../api/jobs";
import { WorkspaceCard } from "../Common/WorkspaceCard";
import { Col } from "../Common/Row";

const ACTIVE: Set<JobStatus> = new Set([
  "pending",
  "running",
  "awaiting_approval",
  "cancelling",
]);

function statusColor(s: JobStatus): "default" | "success" | "error" | "warning" | "info" {
  if (s === "succeeded") return "success";
  if (s === "failed") return "error";
  if (s === "cancelled") return "default";
  if (s === "cancelling") return "warning";
  return "info"; // pending/running/awaiting_approval
}

function relTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function JobRow({ job, onChanged }: { job: JobSummary; onChanged: () => void }) {
  const [busy, setBusy] = React.useState(false);
  const active = ACTIVE.has(job.status);
  const doCancel = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await cancelJob(job.id);
      onChanged();
    } catch {
      /* surfaced by the next refresh; keep quiet */
    } finally {
      setBusy(false);
    }
  };
  return (
    <WorkspaceCard interactive={false} sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        {active && <CircularProgress size={14} thickness={6} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {job.type === "subagent_run" ? "Background task" : job.type}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {relTime(job.started_at || job.created_at)}
            {job.error ? ` — ${job.error}` : ""}
          </Typography>
        </Box>
        <Chip size="small" label={job.status} color={statusColor(job.status)} variant="outlined" />
        {active && (
          <Button size="small" variant="text" color="error" disabled={busy} onClick={doCancel}>
            Cancel
          </Button>
        )}
      </Stack>
    </WorkspaceCard>
  );
}

/**
 * "Running tasks" — background subagent jobs, on the Schedules page. Lists the
 * user's recent jobs (GET /v1/jobs) with status + cancel, and polls while any
 * are active so a running task updates without a manual refresh. Renders
 * nothing when there are no jobs (keeps the page clean for users who never run
 * background work).
 */
export function RunningTasksSection() {
  const [jobs, setJobs] = React.useState<JobSummary[] | null>(null);

  const refresh = React.useCallback(() => {
    listJobs({ type: "subagent_run", limit: 25 }).then(setJobs).catch(() => setJobs([]));
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll while anything is active; stop when all jobs are terminal.
  const hasActive = !!jobs?.some((j) => ACTIVE.has(j.status));
  React.useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [hasActive, refresh]);

  if (jobs === null || jobs.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
        <Activity size={16} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Running tasks
        </Typography>
      </Stack>
      <Col gap={1}>
        {jobs.map((j) => (
          <JobRow key={j.id} job={j} onChanged={refresh} />
        ))}
      </Col>
    </Box>
  );
}
